import { useLocale, useTranslation } from '@multaqa/i18n'
import { Alert, AlertDescription, AlertTitle } from '@multaqa/ui/components/ui/alert'
import { Button } from '@multaqa/ui/components/ui/button'
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@multaqa/ui/components/ui/panel'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useMemo, useState } from 'react'
import type { CompanyOption, CompanyResource, CompanyUpdateInput } from './api'
import { useCompanyOptions, useSetCompanyParent, useUpdateCompany } from './api'
import { isLocale, localizedValues } from './company-formatters'
import { CompanyOverviewForm, CompanyOverviewValues } from './company-overview-content'

type FormValues = {
  legal_name_en: string
  legal_name_ar: string
  trading_name_en: string
  trading_name_ar: string
  code: string
  registration_number: string
  tax_identifier: string
  default_locale: string
  fiscal_year_start_month: string
  parent_id: string
}

function formValues(company: CompanyResource): FormValues {
  return {
    legal_name_en: company.legal_name.en ?? '',
    legal_name_ar: company.legal_name.ar ?? '',
    trading_name_en: company.trading_name?.en ?? '',
    trading_name_ar: company.trading_name?.ar ?? '',
    code: company.code ?? '',
    registration_number: company.registration_number ?? '',
    tax_identifier: company.tax_identifier ?? '',
    default_locale: company.default_locale,
    fiscal_year_start_month: String(company.fiscal_year_start_month),
    parent_id: company.parent_id ?? '',
  }
}

export function parentCandidates(companies: CompanyOption[], companyId: string): CompanyOption[] {
  const byId = new Map(companies.map((company) => [company.id, company]))
  return companies.filter((candidate) => {
    if (candidate.id === companyId) return false
    const visited = new Set<string>()
    let parentId = candidate.parent_id ?? null
    while (parentId !== null && !visited.has(parentId)) {
      if (parentId === companyId) return false
      visited.add(parentId)
      parentId = byId.get(parentId)?.parent_id ?? null
    }
    return true
  })
}

export function CompanyOverview({
  groupId,
  company,
}: {
  groupId: string
  company: CompanyResource
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const options = useCompanyOptions(groupId)
  const update = useUpdateCompany()
  const setParent = useSetCompanyParent()
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState(() => formValues(company))
  const [formError, setFormError] = useState<string | null>(null)
  const candidates = useMemo(
    () => parentCandidates(options.data ?? [], company.id),
    [company.id, options.data],
  )
  const saving = update.isPending || setParent.isPending

  function save() {
    const legalName = localizedValues(values.legal_name_en, values.legal_name_ar)
    if (Object.keys(legalName).length === 0) return setFormError(t('companies.form.nameRequired'))
    if (!values.code.trim()) return setFormError(t('companies.form.codeRequired'))
    if (!isLocale(values.default_locale)) return setFormError(t('companies.form.localeRequired'))
    const month = Number(values.fiscal_year_start_month)
    if (!Number.isInteger(month) || month < 1 || month > 12)
      return setFormError(t('companies.form.monthRequired'))
    const parentChanged = values.parent_id !== (company.parent_id ?? '')
    if (
      parentChanged &&
      values.parent_id &&
      !candidates.some((item) => item.id === values.parent_id)
    )
      return setFormError(t('companies.form.cycleRefused'))
    const tradingName = localizedValues(values.trading_name_en, values.trading_name_ar)
    const input: CompanyUpdateInput = {
      legal_name: legalName,
      trading_name: Object.keys(tradingName).length > 0 ? tradingName : null,
      code: values.code.trim(),
      default_locale: values.default_locale,
      registration_number: values.registration_number.trim() || null,
      tax_identifier: values.tax_identifier.trim() || null,
      fiscal_year_start_month: month,
    }
    update.mutate(
      { groupId, companyId: company.id, input },
      {
        onSuccess: () => {
          if (!parentChanged) return finishSave()
          setParent.mutate(
            { groupId, companyId: company.id, input: { parent_id: values.parent_id || null } },
            {
              onSuccess: finishSave,
              onError: () => setFormError(t('companies.form.parentFailed')),
            },
          )
        },
        onError: () => setFormError(t('companies.form.updateFailed')),
      },
    )
  }
  function finishSave() {
    setEditing(false)
    setFormError(null)
    toast.success(t('state.saved'))
  }
  function cancel() {
    setValues(formValues(company))
    setFormError(null)
    setEditing(false)
  }

  return (
    <Panel data-testid="company-overview-panel">
      <PanelHeader>
        <PanelTitle>{t('companies.title')}</PanelTitle>
        {editing ? (
          <div className="ms-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={cancel}
              disabled={saving}
              data-testid="company-overview-cancel"
            >
              {t('action.cancel')}
            </Button>
            <Button
              type="submit"
              form="company-overview-form"
              size="sm"
              loading={saving}
              disabled={saving}
              data-testid="company-overview-save"
            >
              {t('companies.form.submitEdit')}
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={() => {
              setValues(formValues(company))
              setFormError(null)
              setEditing(true)
            }}
            data-testid="company-overview-edit"
          >
            {t('companies.list.edit')}
          </Button>
        )}
      </PanelHeader>
      <PanelContent className="p-4">
        {formError ? (
          <Alert
            severity="danger"
            className="mb-4"
            role="alert"
            data-testid="company-overview-error"
          >
            <AlertTitle>{t('companies.form.validationTitle')}</AlertTitle>
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        ) : null}
        {editing ? (
          <CompanyOverviewForm
            values={values}
            setValues={setValues}
            company={company}
            candidates={candidates}
            locale={locale}
            optionsLoading={options.isLoading}
            optionsError={options.isError}
            onRetry={() => void options.refetch()}
            onSubmit={save}
          />
        ) : (
          <CompanyOverviewValues company={company} options={options.data ?? []} locale={locale} />
        )}
      </PanelContent>
    </Panel>
  )
}
